import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PawPrint, LogIn, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login, user, bootstrapping } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = location.state?.from && location.state.from !== "/login"
    ? location.state.from
    : "/app";

  // If already authenticated, send straight to the dashboard
  useEffect(() => {
    if (!bootstrapping && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, bootstrapping, navigate, redirectTo]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = formatError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="login-page"
      className="flex min-h-screen items-center justify-center bg-slate-50 p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="login-back-home"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>
        <Card className="rounded-3xl shadow-md">
          <CardContent className="p-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-2xl bg-slate-900 p-3 text-white shadow-sm">
                <PawPrint size={26} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Pawbook</h1>
                <p className="text-sm text-slate-600">Sign in to continue</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block space-y-1 text-sm font-medium">
                Email
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border p-3 font-normal outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="you@example.com"
                />
              </label>
              <label className="block space-y-1 text-sm font-medium">
                Password
                <input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border p-3 font-normal outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="••••••••"
                />
              </label>

              {error && (
                <p data-testid="login-error" className="rounded-xl bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </p>
              )}

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={submitting}
                className="w-full rounded-2xl py-5"
              >
                <LogIn size={16} className="mr-2" />
                {submitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
